"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Input } from "@baluu/ui/components/input";
import { Button } from "@baluu/ui/components/button";
import { Badge } from "@baluu/ui/components/badge";
import {
  MapPin,
  Monitor,
  Tag,
  Loader2,
  ShieldCheck,
  HelpCircle,
  Info,
} from "lucide-react";
import { TermsDialog, useTermsDialog } from "./terms-dialog";
import {
  includedFeesLabel,
} from "@baluu/ui/lib/checkout/booking-fee-disclosure";
import type { TicketPolicyEntry } from "./terms-dialog";
import type {
  TermsSettings,
  CustomTerms,
  TicketBookingPolicy,
} from "./utils/types";
import { TicketPolicyDialog } from "./ticket-selector";
import { BundleMembershipHelpPopup } from "./bundle-membership-help-popup";

type TicketAppliedRule = {
  ruleName: string;
  /** Per-unit price adjustment in minor units. Negative for discounts, positive for surcharges. */
  adjustment: number;
};

type TicketItem = {
  name: string;
  description?: string;
  price: number;
  fullPrice?: number;
  /** Authoritative line total (e.g. from tiered pricing). If provided, overrides price × count. */
  lineTotal?: number;
  count: number;
  ticketTypeGroup?: string;
  /** Explicit deposit flag for V2 `chargeType:"deposit"` tickets, which don't
   *  carry the legacy `ticketTypeGroup:"deposit"`. Falls back to
   *  `ticketTypeGroup === "deposit"` when unset, so V1 deposits are unchanged. */
  isDeposit?: boolean;
  /** V2 deposit rows: the row's deposit due now and full contract for the
   *  whole row (the server's line-level split). When set they replace
   *  `price × count` / `fullPrice × count`, which overstate a tiered line. */
  depositNowTotal?: number;
  depositContractTotal?: number;
  /** How the deposit balance is collected — drives the disclosure sentence. */
  remainderCollection?: "payment_link" | "in_person" | null;
  /** Resolved balance due date (ISO string or Date) for the disclosure line. */
  balanceDueDate?: string | Date | null;
  /** N-instalment plan preview (native plan tickets). `amountLinePence` is the
   *  row's amount for the whole line (preferred); `amountUnitPence` is per-unit
   *  (pre-scaled by sessions) and multiplied by `count` when it is absent. When
   *  exactly one deposit item carries this, the disclosure renders the full
   *  schedule instead of the 2-leg copy. */
  instalmentPreview?: Array<{
    label: string | null;
    amountLinePence?: number;
    amountUnitPence: number;
    dueType: string;
    dueDate: string | null;
    collection: "payment_link" | "in_person";
    offlineNote: string | null;
  }> | null;
  /** LINE-level discount pence per schedule row (payment order) when a code is
   *  applied — the card subtracts so displayed instalments match what each leg
   *  actually collects. Same length as `instalmentPreview`. */
  instalmentDiscountShares?: number[] | null;
  groupName?: string | null;
  policyHint?: string;
  bookingPolicy?: TicketBookingPolicy | null;
  /** Per-unit price before any rule adjustments (in minor units). When set with appliedRules, used to render strike-through original price. */
  originalUnitPrice?: number;
  /** Pricing rules that were applied to this line (V2 tickets). */
  appliedRules?: TicketAppliedRule[];
  /** Multiplier for total savings calculation (e.g. number of selected dates). Defaults to 1. */
  sessionsMultiplier?: number;
};

type CheckoutSummaryCardProps = {
  title: string;
  thumbnail?: string;
  description?: React.ReactNode;
  isOnline?: boolean;
  /** Resolved location name shown instead of the generic "In-person" tag (ignored when isOnline). */
  locationLabel?: string;
  availability?: number;
  hideDisplayAvailableTickets?: boolean;
  items?: TicketItem[];
  total?: number;
  totalRemainingBalance?: number;
  credit?: number;
  discount?: number;
  discountPercentage?: number;
  /**
   * Buyer-side service fee (Task 6/7), already folded into `total`. Renders
   * as a "Service fee" line above the total, plus an "(incl. … service fee)"
   * note beside the Total row itself — DMCC Act 2025 requires the mandatory
   * fee to be visible from the first step (this sidebar renders on every
   * step, not just the payment step). Always 0/undefined on non-Baluu-hosted
   * surfaces, free bookings, and schools with no fee configured.
   */
  serviceFeePence?: number;
  /**
   * School-set booking fee (customer-paid), already folded into `total`.
   * Renders as a "Booking fee" line above the "Service fee" line, plus is
   * summed into the "(incl. … fee)" note beside the Total row itself.
   * Always 0/undefined on free bookings and schools with no fee configured.
   */
  bookingFeePence?: number;
  /** Rate + refund wording for the "Booking fee" row's tooltip. */
  bookingFeeDetail?: string | null;
  isDiscountEnabled?: boolean;
  isDiscountInvalid?: boolean;
  discountError?: string;
  onApplyDiscount?: (code: string) => void;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: CustomTerms;
  /** Timezone for date formatting */
  timezone?: string;
  /** Per-ticket booking policies for dialog display */
  ticketPolicies?: TicketPolicyEntry[];
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
    ticketPolicies?: TicketPolicyEntry[];
  }) => void;
  onPolicyClick?: (ticketName: string, policy: TicketBookingPolicy) => void;
  personalDetails?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  bundleInfo?: {
    ticketCount: number;
  };
  /** Number of selected dates (multi-date mode) */
  dateCount?: number;
  /** Total multiplied by dateCount (multi-date mode) */
  multiDateTotal?: number;
  /** When true, suppress the "N × £perDate" breakdown line above the total.
   *  Use when per-rate ticket rows already convey the rate breakdown so the
   *  per-date average would be misleading. */
  hidePerDateBreakdown?: boolean;
  currency?: string;
  className?: string;
  showBundleMembershipHelp?: boolean;
};

function formatPrice(price: number, currency: string = "GBP"): string {
  const symbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const symbol = symbols[currency] || currency;
  return `${symbol}${(price / 100).toFixed(2)}`;
}

function formatBalanceDueDate(
  value: string | Date | null | undefined,
  timezone?: string,
): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(d);
}

function CheckoutSummaryCard({
  title,
  thumbnail,
  description,
  isOnline = false,
  locationLabel,
  availability,
  hideDisplayAvailableTickets = false,
  items = [],
  total,
  totalRemainingBalance = 0,
  credit = 0,
  discount = 0,
  discountPercentage,
  serviceFeePence = 0,
  bookingFeePence = 0,
  bookingFeeDetail = null,
  isDiscountEnabled = false,
  isDiscountInvalid = false,
  discountError,
  onApplyDiscount,
  cancellationPolicyUrl,
  termsSettings,
  customTerms,
  timezone,
  onOpenTermsDialog,
  ticketPolicies,
  onPolicyClick,
  personalDetails,
  bundleInfo,
  dateCount,
  multiDateTotal,
  hidePerDateBreakdown = false,
  currency = "GBP",
  className,
  showBundleMembershipHelp = false,
}: CheckoutSummaryCardProps) {
  const [discountCode, setDiscountCode] = React.useState("");
  const [isApplyingDiscount, setIsApplyingDiscount] = React.useState(false);
  const termsDialog = useTermsDialog();
  const [isBundleHelpOpen, setBundleHelpOpen] = React.useState(false);
  const [policyDialog, setPolicyDialog] = React.useState<{
    isOpen: boolean;
    ticketName: string;
    policy: TicketBookingPolicy | null;
  }>({ isOpen: false, ticketName: "", policy: null });

  // Check if we have terms settings to show dialog
  const hasTermsSettings =
    termsSettings?.cancellationPolicy ||
    termsSettings?.reschedulePolicy ||
    termsSettings?.termsAndConditions;

  const hasItems = items.length > 0 && items.some((item) => item.count > 0);
  const isDepositItem = (item: TicketItem) =>
    item.isDeposit ?? item.ticketTypeGroup === "deposit";
  const depositItems = items.filter(isDepositItem);
  const hasDepositTicket = depositItems.length > 0;
  // Only surface a concrete collection method / due date when EVERY deposit line
  // agrees. With a mixed-deposit cart one sentence would misrepresent the rest,
  // so we fall back to just the aggregate "Remaining balance" amount above.
  const uniformRemainderCollection =
    hasDepositTicket &&
    depositItems.every(
      (i) => i.remainderCollection === depositItems[0]!.remainderCollection,
    )
      ? depositItems[0]!.remainderCollection
      : null;
  const uniformBalanceDueDate =
    hasDepositTicket &&
    depositItems.every(
      (i) =>
        String(i.balanceDueDate ?? "") ===
        String(depositItems[0]!.balanceDueDate ?? ""),
    )
      ? depositItems[0]!.balanceDueDate
      : null;

  const handleApplyDiscount = async () => {
    if (!onApplyDiscount || !discountCode.trim()) return;
    setIsApplyingDiscount(true);
    try {
      await onApplyDiscount(discountCode);
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  return (
    <div
      className={cn(
        "bg-card rounded-lg border border-border overflow-hidden shadow-sm",
        className,
      )}
    >
      {/* Header with thumbnail - shorter on mobile/tablet, taller on desktop sidebar */}
      {thumbnail && (
        <div className="aspect-[3/1] lg:aspect-video overflow-hidden">
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Title and description */}
      <div className="p-4 border-b border-border/50">
        <h3 className="font-bold text-foreground uppercase tracking-wide">
          {title}
        </h3>
        {description && (
          <div className="text-sm text-muted-foreground mt-2">
            {description}
          </div>
        )}
        <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
          {isOnline ? (
            <span className="flex items-center gap-1">
              <Monitor className="w-4 h-4" />
              Online
            </span>
          ) : (
            <span className="flex items-center gap-1 min-w-0">
              <MapPin className="w-4 h-4 shrink-0" />
              <span className="truncate">{locationLabel || "In-person"}</span>
            </span>
          )}
          {availability !== undefined && availability > 0 && (
            <span className="text-primary font-medium">
              {hideDisplayAvailableTickets
                ? "Spots available"
                : `${availability} spots left`}
            </span>
          )}
        </div>
      </div>

      {/* Items */}
      {hasItems && (
        <div className="border-b border-border/50">
          {items
            .filter((item) => item.count > 0)
            .map((item, index) => {
              const isDeposit = isDepositItem(item);
              const sessionsMultiplier = item.sessionsMultiplier ?? 1;
              // `item.lineTotal` is the row's full contribution to the order
              // total (sessions × qty × per-session price for non-flat tickets,
              // already-aggregate for flat-per-order/session). Divide by
              // sessionsMultiplier for the per-date display value.
              const depositPrice =
                isDeposit && item.depositNowTotal !== undefined
                  ? item.depositNowTotal
                  : !isDeposit &&
                      item.lineTotal !== undefined &&
                      sessionsMultiplier > 0
                    ? Math.round(item.lineTotal / sessionsMultiplier)
                    : item.price * item.count;
              const fullPriceTotal =
                isDeposit && item.depositContractTotal !== undefined
                  ? item.depositContractTotal
                  : (item.fullPrice ?? item.price) * item.count;
              const isMultiDate = dateCount !== undefined && dateCount > 1;

              const rulesApplied = item.appliedRules ?? [];
              const hasRuleDiscount =
                !isDeposit && rulesApplied.some((r) => r.adjustment < 0);
              // Per-row date count drives the "applied to N dates" subtitle so
              // a per-rate split row (e.g. Friday-only Weekend Premium across
              // 1 of 2 selected dates) shows 1, not the cart-wide dateCount.
              const itemDateCount = item.sessionsMultiplier ?? dateCount ?? 1;
              const showAppliedDates = isMultiDate && itemDateCount > 1;
              const originalUnitDisplay = item.originalUnitPrice ?? item.price;
              // Per-unit per-date price for strike-through comparison and
              // unit-level display. lineTotal already aggregates sessions ×
              // qty, so divide by both for the per-unit-per-date figure.
              const displayedUnitPrice =
                !isDeposit &&
                item.lineTotal !== undefined &&
                item.count > 0 &&
                sessionsMultiplier > 0
                  ? Math.round(item.lineTotal / item.count / sessionsMultiplier)
                  : item.price;
              const totalRuleSavings = hasRuleDiscount
                ? rulesApplied.reduce(
                    (sum, r) =>
                      sum +
                      (r.adjustment < 0
                        ? -r.adjustment * item.count * sessionsMultiplier
                        : 0),
                    0,
                  )
                : 0;

              return (
                <div key={index} className="px-4 py-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">
                        {item.count} x{" "}
                        {item.groupName
                          ? `${item.groupName}: ${item.name}`
                          : item.name}
                      </p>
                      {showAppliedDates && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          applied to {itemDateCount}{" "}
                          {itemDateCount === 1 ? "date" : "dates"}
                        </p>
                      )}
                      {item.description && (
                        <p className="text-sm text-muted-foreground">
                          {item.description}
                        </p>
                      )}
                      {item.policyHint && (
                        <p className="text-xs text-muted-foreground/70 mt-0.5 flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3 shrink-0" />
                          {item.policyHint}
                        </p>
                      )}
                      {item.bookingPolicy && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onPolicyClick) {
                              onPolicyClick(item.name, item.bookingPolicy!);
                            } else {
                              setPolicyDialog({
                                isOpen: true,
                                ticketName: item.name,
                                policy: item.bookingPolicy!,
                              });
                            }
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 font-medium mt-1 text-left transition-colors"
                        >
                          Cancellation Policy
                        </button>
                      )}
                    </div>
                    {!bundleInfo && (
                      <div className="text-right shrink-0">
                        {hasRuleDiscount &&
                          originalUnitDisplay > displayedUnitPrice && (
                            <p className="text-xs text-muted-foreground line-through leading-tight">
                              {formatPrice(originalUnitDisplay, currency)}
                            </p>
                          )}
                        <span className="font-medium text-foreground">
                          {isDeposit && item.fullPrice ? (
                            <>
                              {formatPrice(depositPrice, currency)}/
                              <span className="text-muted-foreground">
                                {formatPrice(fullPriceTotal, currency)}
                              </span>
                            </>
                          ) : (
                            formatPrice(depositPrice, currency)
                          )}
                        </span>
                        {isMultiDate && (
                          <p className="text-xs text-muted-foreground">
                            per date
                          </p>
                        )}
                        {hasRuleDiscount && totalRuleSavings > 0 && (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-0.5">
                            You save {formatPrice(totalRuleSavings, currency)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {hasRuleDiscount && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {rulesApplied
                        .filter((r) => r.adjustment < 0)
                        .map((r, rIdx) => (
                          <Badge
                            key={rIdx}
                            variant="secondary"
                            className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 border-0 font-medium"
                          >
                            <Tag className="h-3 w-3 mr-1" />
                            {r.ruleName}
                          </Badge>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Bundle Info */}
      {bundleInfo && hasItems && (
        <div className="px-4 py-3 border-b border-border/50 bg-blue-100 dark:bg-blue-900">
          <p className="text-sm text-foreground font-medium">
            We will deduct {bundleInfo.ticketCount}{" "}
            {bundleInfo.ticketCount === 1 ? "ticket" : "tickets"} from your
            bundle
          </p>
        </div>
      )}

      {/* Credit */}
      {credit > 0 && (
        <div className="px-4 py-3 border-b border-border/50 bg-green-100 dark:bg-green-900">
          <div className="flex justify-between items-center">
            <span className="text-sm text-green-500">Credit applied</span>
            <span className="font-medium text-green-500">
              -{formatPrice(credit, currency)}
            </span>
          </div>
        </div>
      )}

      {/* Discount */}
      {isDiscountEnabled && credit === 0 && (
        <div className="px-4 py-3 border-b border-border/50">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Discount code"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={handleApplyDiscount}
              disabled={isApplyingDiscount || !discountCode.trim()}
            >
              {isApplyingDiscount ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Apply"
              )}
            </Button>
          </div>
          {discountError && (
            <p className="text-sm text-destructive mt-2">{discountError}</p>
          )}
        </div>
      )}

      {/* Applied Discount */}
      {discount > 0 && (
        <div className="px-4 py-3 border-b border-border/50 bg-green-100 dark:bg-green-900">
          <div className="flex justify-between items-center">
            <span className="text-sm  flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Discount applied
              {discountPercentage && (
                <Badge variant="secondary" className="text-xs">
                  {discountPercentage}% off
                </Badge>
              )}
            </span>
            <span className="font-medium text-green-500">
              -{formatPrice(discount, currency)}
            </span>
          </div>
        </div>
      )}

      {/* Booking fee (Task 13 — school-set customer-paid fee). Renders ABOVE
       *  the service fee, on every checkout step like the service fee below. */}
      {bookingFeePence > 0 && (
        <div className="px-4 py-3 border-b border-border/50 flex justify-between text-sm">
          <span className="text-muted-foreground flex items-center gap-1">
            Booking fee
            {bookingFeeDetail && (
              <span title={bookingFeeDetail} className="inline-flex">
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">{bookingFeeDetail}</span>
              </span>
            )}
          </span>
          <span>{formatPrice(bookingFeePence, currency)}</span>
        </div>
      )}

      {/* Service fee (Task 7 — DMCC Act 2025 up-front disclosure). This card
       *  renders on every checkout step (not just payment), so this line is
       *  visible from the first step where a price is listed. */}
      {serviceFeePence > 0 && (
        <div className="px-4 py-3 border-b border-border/50 flex justify-between text-sm">
          <span className="text-muted-foreground">Service fee</span>
          <span>{formatPrice(serviceFeePence, currency)}</span>
        </div>
      )}

      {/* Personal Details */}
      {personalDetails && (
        <div className="px-4 py-3 border-b border-border/50">
          <p className="font-medium text-foreground">
            {personalDetails.firstName} {personalDetails.lastName}
          </p>
          <p className="text-sm text-muted-foreground">
            {personalDetails.email}
          </p>
        </div>
      )}

      {/* Total */}
      {hasItems && total !== undefined && !bundleInfo && (
        <div className="px-4 py-3 bg-background border-t border-border">
          {dateCount &&
          dateCount > 1 &&
          multiDateTotal !== undefined &&
          !hidePerDateBreakdown ? (
            <>
              <div className="flex justify-between items-center text-sm text-muted-foreground mb-1">
                <span>
                  {dateCount} dates &times;{" "}
                  {formatPrice(
                    Math.round(multiDateTotal / dateCount),
                    currency,
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-foreground">Total</span>
                <span className="font-bold text-lg text-foreground">
                  {formatPrice(multiDateTotal, currency)}
                </span>
              </div>
            </>
          ) : dateCount &&
            dateCount > 1 &&
            multiDateTotal !== undefined &&
            hidePerDateBreakdown ? (
            <div className="flex justify-between items-center">
              <span className="font-semibold text-foreground">Total</span>
              <span className="font-bold text-lg text-foreground">
                {formatPrice(multiDateTotal, currency)}
              </span>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <span className="font-semibold text-foreground">
                {hasDepositTicket ? "Total Deposit" : "Total"}
              </span>
              <span className="flex items-baseline gap-1.5">
                <span className="font-bold text-lg text-foreground">
                  {formatPrice(total, currency)}
                </span>
                {(bookingFeePence > 0 || serviceFeePence > 0) && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    (incl.{" "}
                    {formatPrice(bookingFeePence + serviceFeePence, currency)}{" "}
                    {includedFeesLabel(bookingFeePence, serviceFeePence)})
                  </span>
                )}
              </span>
            </div>
          )}
          {hasDepositTicket && totalRemainingBalance > 0 && (
            <div className="flex justify-between items-center mt-2 text-muted-foreground">
              <span className="font-medium">Remaining balance</span>
              <span className="font-medium">
                {formatPrice(totalRemainingBalance, currency)}
              </span>
            </div>
          )}
          {hasDepositTicket && totalRemainingBalance > 0 && (
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              {(() => {
                // N-instalment plan: when EXACTLY ONE deposit line carries a
                // schedule, spell the whole plan out. Multiple plan lines fall
                // back to the aggregate 2-leg copy below (no cross-item
                // schedule merging), same spirit as the uniform-* rule.
                const planItems = depositItems.filter(
                  (i) => (i.instalmentPreview?.length ?? 0) >= 2,
                );
                if (planItems.length === 1 && depositItems.length === 1) {
                  const item = planItems[0]!;
                  const rows = item.instalmentPreview!;
                  // Discounted schedule: subtract each row's share of an applied
                  // code (same split the server stamps on the rows) — otherwise
                  // the plan lines contradict the discounted totals above.
                  const shares = item.instalmentDiscountShares;
                  return (
                    <>
                      <p className="font-medium">Payment plan:</p>
                      {rows.map((row, k) => {
                        const amount = formatPrice(
                          Math.max(
                            0,
                            (row.amountLinePence ?? row.amountUnitPence * item.count) -
                              (shares?.[k] ?? 0),
                          ),
                          currency,
                        );
                        if (k === 0) {
                          return <p key={k}>{amount} today.</p>;
                        }
                        const dueStr = formatBalanceDueDate(row.dueDate, timezone);
                        const method =
                          row.collection === "payment_link"
                            ? "we'll email you a secure payment link"
                            : row.offlineNote || "pay at the venue";
                        const when = dueStr
                          ? row.dueType === "on_arrival"
                            ? ` due on arrival (${dueStr})`
                            : ` due on ${dueStr}`
                          : row.dueType === "on_arrival"
                            ? " due on arrival"
                            : "";
                        return (
                          <p key={k}>
                            {amount}
                            {when} — {method}.
                          </p>
                        );
                      })}
                    </>
                  );
                }
                const dueStr = formatBalanceDueDate(
                  uniformBalanceDueDate,
                  timezone,
                );
                return (
                  <>
                    {dueStr ? <p>Balance due on {dueStr}.</p> : null}
                    {uniformRemainderCollection === "payment_link" && (
                      <p>We&apos;ll email you a secure payment link.</p>
                    )}
                    {uniformRemainderCollection === "in_person" && (
                      <p>Pay the balance at the venue.</p>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Bundle/membership help link */}
      {showBundleMembershipHelp && (
        <div className="px-4 py-3 border-t border-border/50">
          <button
            type="button"
            onClick={() => setBundleHelpOpen(true)}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 text-left"
          >
            <HelpCircle className="w-4 h-4" />
            Have a bundle or membership? Learn more
          </button>
        </div>
      )}

      {/* Terms Dialog */}
      <TermsDialog
        isOpen={termsDialog.isOpen}
        onClose={termsDialog.close}
        settings={termsSettings}
        customTerms={customTerms}
        timezone={timezone}
        ticketPolicies={ticketPolicies}
      />

      {showBundleMembershipHelp && (
        <BundleMembershipHelpPopup
          open={isBundleHelpOpen}
          onOpenChange={setBundleHelpOpen}
        />
      )}

      {/* Per-ticket policy dialog (inline, for non-webcomponents) */}
      {!onPolicyClick && policyDialog.policy && (
        <TicketPolicyDialog
          isOpen={policyDialog.isOpen}
          onClose={() =>
            setPolicyDialog((prev) => ({ ...prev, isOpen: false }))
          }
          ticketName={policyDialog.ticketName}
          policy={policyDialog.policy}
        />
      )}
    </div>
  );
}

export { CheckoutSummaryCard };
export type { CheckoutSummaryCardProps, TicketItem };
